/**
 * Agregados del Mapa de Talento para "Analytics people" (doc People 02/09).
 *
 * Vive en shared/ a propósito (mismo patrón que dtalk-stats): el backend
 * devuelve el universo joineado (community map activos + records del ciclo) y
 * el front recalcula KPIs, curva e insights sobre las filas FILTRADAS sin
 * pegarle de nuevo a la API. Una sola definición de qué es "completada",
 * "sin completar" y de cada insight.
 */

import { addMonthsISO, todayAR } from './dates';

export interface TalentAnalyticsRow {
  email: string;
  /** ID de nómina (columna A del Community Map). */
  employeeId?: number | null;
  name: string;
  lastName: string;
  /** Manager ACTUAL (community map); si no resuelve, el evaluador del record o el texto crudo de peopleManager. */
  leader: string | null;
  impactLevel: string | null;
  area: string | null;
  country: string | null;
  clapbase: string | null;
  eligibilityStatus: string;   // eligible | onboarding | inactive | license
  processStatus: string;       // not_started | draft | ... | auto_assigned
  finalCategory: string | null;
  /** Modificaciones de categoría no-líder (HRBP + sistema). */
  modificationsCount: number;
  /** Fecha de ingreso (ISO yyyy-mm-dd, del community map). Euge 24/09: Tops con < 6 meses. */
  startDate?: string | null;
  /** Última promoción efectiva (ISO yyyy-mm-dd, planilla de promociones). Euge 24/09: Tops recién promovidos. */
  lastPromotionDate?: string | null;
}

/**
 * Licencia (Sol 18/09, copiado de D-Talk): la persona está fuera del ciclo.
 * No se le pide evaluación, no cuenta en el % que falta completar ni en la
 * curva. Se marca a mano desde "Gestionar licencias".
 */
export function isOnLicense(r: Pick<TalentAnalyticsRow, 'eligibilityStatus'>): boolean {
  return r.eligibilityStatus === 'license';
}

/** Evaluación COMPLETADA = el líder (o HRBP) cerró la categoría. Las
 *  auto-asignadas por el sistema NO cuentan como completadas. */
export function isCompleted(r: Pick<TalentAnalyticsRow, 'processStatus'>): boolean {
  return r.processStatus === 'confirmed' || r.processStatus === 'hrbp_modified';
}

export type TalentCurveKey =
  | 'onboarding'
  | 'licencia'
  | 'low_performance'
  | 'cumple_sin_confirmar'
  | 'cumple_con_oportunidades'
  | 'performance_solida'
  | 'top_performer';

/**
 * Orden del eje — los 7 estados que pidió Sol (21/09/2026): Onboarding, Licencia,
 * Low performance, Cumple · Sin confirmar, Cumple, Performance sólida, Top.
 * "Sin evaluar" ya no existe como estado: todo lo no confirmado es "Cumple con
 * oportunidades · Sin confirmar" (ver curveBucket).
 */
export const CURVE_ORDER: TalentCurveKey[] = [
  'onboarding',
  'licencia',
  'low_performance',
  'cumple_sin_confirmar',
  'cumple_con_oportunidades',
  'performance_solida',
  'top_performer',
];

/**
 * A qué punto de la curva va cada persona. ÚNICA definición: la usan Analytics
 * people, el panel del Mapa del equipo y el chip de la tabla.
 *
 * Una categoría cuenta como tal SOLO si la evaluación está completada. Antes
 * se miraba `finalCategory` a secas, y como el cron deja a todo el mundo en
 * `auto_assigned` con "Cumple con oportunidades", el gráfico decía que casi
 * toda la compañía cumplía cuando en realidad casi nadie había sido evaluado
 * (Sol, 14/09: "parece que todos efectivamente tienen esa categoría y quizá
 * solo está sin confirmar"). Esos van a `cumple_sin_confirmar`.
 *
 * 21/09 (Sol): NO hay "Sin evaluar". Lo que no está confirmado —la propuesta del
 * sistema, un borrador con cualquier categoría, o alguien sin evaluación creada
 * (p. ej. sin líder)— es "Cumple con oportunidades · Sin confirmar": la categoría
 * por defecto hasta que el líder confirme. Licencia es un estado propio.
 */
export function curveBucket(
  r: Pick<TalentAnalyticsRow, 'eligibilityStatus' | 'processStatus' | 'finalCategory'>,
): TalentCurveKey {
  if (isOnLicense(r)) return 'licencia';
  if (r.eligibilityStatus === 'onboarding') return 'onboarding';
  if (isCompleted(r)) {
    switch (r.finalCategory) {
      case 'low_performance':
      case 'cumple_con_oportunidades':
      case 'performance_solida':
      case 'top_performer':
        return r.finalCategory;
    }
  }
  return 'cumple_sin_confirmar';
}

export interface TalentCurveCount {
  key: TalentCurveKey;
  count: number;
  pct: number;
}

/** Avance de un área: quiénes aplican (sin onboarding), cuántos cerrados. */
export interface TalentAreaProgress {
  area: string;
  /** Personas del área que aplican al ciclo (excluye onboarding). */
  applicable: number;
  completed: number;
  pct: number;
  onboarding: number;
}

export interface TalentInsight {
  /** Id estable → template i18n performance.talent_map.analytics.insight_<id> */
  id: string;
  group: string;
  count: number;
  pct: number;
}

/** Ventana de "reciente" para los Tops a mirar de cerca (Euge 24/09: 6 meses). */
export const RECENT_TOP_MONTHS = 6;

/** Un Top Performer confirmado que ingresó o fue promovido dentro de la ventana. */
export interface TalentTopWatch {
  email: string;
  name: string;
  lastName: string;
  area: string | null;
  leader: string | null;
  /** Ingreso o promoción efectiva (ISO). */
  date: string;
}

export interface TalentMapPeopleStats {
  /** Universo visible (incluye onboarding; excluye licencias). */
  total: number;
  /** Personas en licencia: fuera del ciclo, no cuentan en nada más. */
  licencia: number;
  /**
   * Quienes APLICAN al ciclo: total menos onboarding. Es el denominador de
   * los % (Sol, 14/09: "los que son onboarding no aplican").
   */
  applicable: number;
  onboarding: number;
  completed: number;
  completedPct: number;
  uncompleted: number;
  uncompletedPct: number;
  top: number;
  topPct: number;
  low: number;
  lowPct: number;
  curve: TalentCurveCount[];
  /** Avance por área, de mayor a menor % (para el gráfico de barras). */
  byArea: TalentAreaProgress[];
  insights: TalentInsight[];
  /** Desde qué fecha (ISO) algo cuenta como "reciente": hoy − RECENT_TOP_MONTHS. */
  recentSince: string;
  /** Tops confirmados con menos de RECENT_TOP_MONTHS meses de ingreso, más nuevos primero. */
  topRecentHires: TalentTopWatch[];
  /** Tops confirmados promovidos hace menos de RECENT_TOP_MONTHS meses, más recientes primero. */
  topRecentlyPromoted: TalentTopWatch[];
}

/** Universo mínimo para insights de %: evita "el área con 1 persona tiene 100%". */
const MIN_GROUP = 3;

const pctOf = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

function groupBy(rows: TalentAnalyticsRow[], key: (r: TalentAnalyticsRow) => string | null) {
  const map = new Map<string, TalentAnalyticsRow[]>();
  for (const r of rows) {
    const k = (key(r) ?? '').trim();
    if (!k) continue;
    const list = map.get(k) ?? [];
    list.push(r);
    map.set(k, list);
  }
  return map;
}

/** Grupo con MÁS unidades absolutas de `metric`. Empate ⇒ el primero; 0 ⇒ null. */
function maxAbsolute(
  groups: Map<string, TalentAnalyticsRow[]>,
  metric: (rows: TalentAnalyticsRow[]) => number,
): { group: string; count: number; pct: number } | null {
  let best: { group: string; count: number; pct: number } | null = null;
  for (const [group, rows] of groups) {
    const count = metric(rows);
    if (count > 0 && (!best || count > best.count)) {
      best = { group, count, pct: pctOf(count, rows.length) };
    }
  }
  return best;
}

/** Grupo con mayor % de `predicate` sobre SU PROPIO universo (doc: priorizar
 *  porcentajes salvo los "con más"). Grupos < MIN_GROUP no compiten. */
function maxPct(
  groups: Map<string, TalentAnalyticsRow[]>,
  predicate: (r: TalentAnalyticsRow) => boolean,
): { group: string; count: number; pct: number } | null {
  let best: { group: string; count: number; pct: number } | null = null;
  for (const [group, rows] of groups) {
    if (rows.length < MIN_GROUP) continue;
    const count = rows.filter(predicate).length;
    const pct = pctOf(count, rows.length);
    if (count > 0 && (!best || pct > best.pct)) {
      best = { group, count, pct };
    }
  }
  return best;
}

/**
 * @param today Fecha de referencia (ISO). Default: hoy en Argentina. Parámetro
 * para que el test fije la ventana de "reciente".
 */
export function buildTalentMapStats(allRows: TalentAnalyticsRow[], today: string = todayAR()): TalentMapPeopleStats {
  // Licencia sale del universo antes de contar nada (ver isOnLicense).
  const licencia = allRows.filter(isOnLicense).length;
  const rows = allRows.filter(r => !isOnLicense(r));
  const total = rows.length;
  const isOnb = (r: TalentAnalyticsRow) => r.eligibilityStatus === 'onboarding';

  // Denominador de los %: onboarding no aplica al ciclo. Antes era `total` y el
  // KPI decía "24% · 87 de 357" con 47 personas que no tenían que ser evaluadas.
  const onboarding = rows.filter(isOnb).length;
  const applicable = total - onboarding;

  const completed = rows.filter(r => !isOnb(r) && isCompleted(r)).length;
  const uncompleted = applicable - completed;

  // Una categoría cuenta solo confirmada (ver curveBucket).
  const bucketCount = (key: TalentCurveKey) => rows.filter(r => curveBucket(r) === key).length;
  const top = bucketCount('top_performer');
  const low = bucketCount('low_performance');

  // Curva sobre TODO el universo visible: onboarding y licencia son puntos del
  // eje (Sol 21/09); los KPIs de arriba siguen sin contarlos.
  const curve: TalentCurveCount[] = CURVE_ORDER.map(key => {
    const count = allRows.filter(r => curveBucket(r) === key).length;
    return { key, count, pct: pctOf(count, allRows.length) };
  });

  // Avance por área: % de cerradas sobre quienes aplican en cada área
  const areaProgress: TalentAreaProgress[] = [...groupBy(rows, r => r.area).entries()]
    .map(([area, rs]) => {
      const onb = rs.filter(isOnb).length;
      const app = rs.length - onb;
      const done = rs.filter(r => !isOnb(r) && isCompleted(r)).length;
      return { area, applicable: app, completed: done, pct: pctOf(done, app), onboarding: onb };
    })
    .filter(a => a.applicable > 0)
    .sort((a, b) => b.pct - a.pct || b.applicable - a.applicable || a.area.localeCompare(b.area, 'es'));

  // ── Insights (doc §7): mínimos + los que salen gratis del mismo agregado ──
  const byArea = groupBy(rows, r => r.area);
  const byLeader = groupBy(rows, r => r.leader);
  const byClapbase = groupBy(rows, r => r.clapbase);
  const byCountry = groupBy(rows, r => r.country);
  const byLevel = groupBy(rows, r => r.impactLevel);

  const isCat = (cat: string) => (r: TalentAnalyticsRow) => curveBucket(r) === cat;
  const leaderShort = (x: { group: string; count: number; pct: number } | null) =>
    x ? { ...x, group: x.group.includes('@') ? x.group.split('@')[0] : x.group } : null;

  const candidates: Array<[string, { group: string; count: number; pct: number } | null]> = [
    ['area_most_top',             maxAbsolute(byArea, rs => rs.filter(isCat('top_performer')).length)],
    ['leader_most_top',           leaderShort(maxAbsolute(byLeader, rs => rs.filter(isCat('top_performer')).length))],
    ['area_pct_low',              maxPct(byArea, isCat('low_performance'))],
    ['leader_pct_low',            leaderShort(maxPct(byLeader, isCat('low_performance')))],
    ['leader_most_modifications', leaderShort(maxAbsolute(byLeader, rs => rs.reduce((a, r) => a + (r.modificationsCount || 0), 0)))],
    ['area_pct_completed',        maxPct(byArea, isCompleted)],
    ['area_pct_uncompleted',      maxPct(byArea, r => !isCompleted(r))],
    ['clapbase_pct_solida',       maxPct(byClapbase, isCat('performance_solida'))],
    ['leader_pct_cumple',         leaderShort(maxPct(byLeader, isCat('cumple_con_oportunidades')))],
    ['level_pct_top',             maxPct(byLevel, isCat('top_performer'))],
    ['level_pct_low',             maxPct(byLevel, isCat('low_performance'))],
    ['country_pct_top',           maxPct(byCountry, isCat('top_performer'))],
    ['country_pct_low',           maxPct(byCountry, isCat('low_performance'))],
  ];

  const insights: TalentInsight[] = candidates
    .filter((c): c is [string, { group: string; count: number; pct: number }] => !!c[1])
    .map(([id, v]) => ({ id, ...v }));

  // ── Tops a mirar de cerca (Euge 24/09) ─────────────────────────────────────
  // Solo Tops CONFIRMADOS (curveBucket), con ingreso o promoción dentro de la
  // ventana: "menos de 6 meses" = después de hoy − 6 meses (el día exacto no
  // cuenta). Sin fecha ⇒ no entra (no se adivina).
  const recentSince = addMonthsISO(today, -RECENT_TOP_MONTHS);
  const tops = rows.filter(isCat('top_performer'));
  const watch = (dateOf: (r: TalentAnalyticsRow) => string | null | undefined): TalentTopWatch[] =>
    tops
      .flatMap(r => {
        const date = dateOf(r);
        return date && date > recentSince && date <= today
          ? [{ email: r.email, name: r.name, lastName: r.lastName, area: r.area, leader: r.leader, date }]
          : [];
      })
      .sort((a, b) => b.date.localeCompare(a.date) || a.lastName.localeCompare(b.lastName, 'es'));
  const topRecentHires = watch(r => r.startDate);
  const topRecentlyPromoted = watch(r => r.lastPromotionDate);

  return {
    total,
    licencia,
    applicable,
    onboarding,
    completed,
    completedPct: pctOf(completed, applicable),
    uncompleted,
    uncompletedPct: pctOf(uncompleted, applicable),
    top,
    topPct: pctOf(top, applicable),
    low,
    lowPct: pctOf(low, applicable),
    curve,
    byArea: areaProgress,
    insights,
    recentSince,
    topRecentHires,
    topRecentlyPromoted,
  };
}
